import React, { useState, useEffect } from 'react';
import { Sparkles, X, ChevronRight, TrendingUp, AlertTriangle, Lightbulb, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeaders } from '../utils/auth';
import { useVisibility } from '../hooks/useVisibility';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProactiveAssistant = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());
  const [minimized, setMinimized] = useState(true);
  const navigate = useNavigate();
  const isVisible = useVisibility();

  // Only show when authenticated
  const isAuthenticated = !!localStorage.getItem('lee_token');

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    if (!isVisible) return undefined;
    fetchSuggestions();
    // Refresh suggestions every 5 minutes
    const interval = setInterval(fetchSuggestions, 5 * 60 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const fetchSuggestions = async () => {
    try {
      // Fetch user context
      const [inventions, tasks, notifications] = await Promise.all([
        axios.get(`${API}/inventions`, { headers: getAuthHeaders() }).catch(() => ({ data: { inventions: [] } })),
        axios.get(`${API}/progress/active-tasks`, { headers: getAuthHeaders() }).catch(() => ({ data: { tasks: [] } })),
        axios.get(`${API}/notifications/unread`, { headers: getAuthHeaders() }).catch(() => ({ data: { unread_count: 0 } }))
      ]);

      const generatedSuggestions = [];

      // Suggestion 1: Unread notifications
      if (notifications.data.unread_count > 0) {
        generatedSuggestions.push({
          id: 'unread-notifications',
          type: 'action',
          priority: 'high',
          icon: AlertTriangle,
          title: `You have ${notifications.data.unread_count} unread notifications`,
          description: 'Review notifications to stay updated on your projects',
          action: () => navigate('/notifications'),
          actionLabel: 'View Notifications'
        });
      }

      // Suggestion 2: Stalled tasks
      const stalledTasks = (tasks.data.tasks || []).filter(t => 
        t.progress_percent < 50 && 
        new Date() - new Date(t.started_at) > 24 * 60 * 60 * 1000
      );
      if (stalledTasks.length > 0) {
        generatedSuggestions.push({
          id: 'stalled-tasks',
          type: 'insight',
          priority: 'medium',
          icon: TrendingUp,
          title: `${stalledTasks.length} task(s) need attention`,
          description: 'Some tasks haven\'t progressed in over 24 hours',
          action: () => navigate('/progress'),
          actionLabel: 'View Progress'
        });
      }

      // Suggestion 3: No active inventions
      if ((inventions.data.inventions || []).length === 0) {
        generatedSuggestions.push({
          id: 'create-first-invention',
          type: 'opportunity',
          priority: 'high',
          icon: Lightbulb,
          title: 'Start your first invention',
          description: 'Begin bringing your On Point Jar idea to life',
          action: () => navigate('/inventions/new'),
          actionLabel: 'Create Invention'
        });
      }

      // Suggestion 4: Incomplete inventions
      const incompleteInventions = (inventions.data.inventions || []).filter(inv => 
        inv.stage === 'concept' || inv.stage === 'validation'
      );
      if (incompleteInventions.length > 0) {
        generatedSuggestions.push({
          id: 'complete-validation',
          type: 'action',
          priority: 'medium',
          icon: Target,
          title: `${incompleteInventions.length} invention(s) need validation`,
          description: 'Complete technical and market validation to move forward',
          action: () => navigate('/creation'),
          actionLabel: 'View Inventions'
        });
      }

      // Suggestion 5: Daily progress check
      const now = new Date();
      if (now.getHours() >= 9 && now.getHours() <= 10) {
        generatedSuggestions.push({
          id: 'daily-check',
          type: 'insight',
          priority: 'low',
          icon: Sparkles,
          title: 'Good morning! Here\'s your day ahead',
          description: `${tasks.data.tasks?.length || 0} active tasks, ${notifications.data.unread_count} updates`,
          action: () => navigate('/dashboard'),
          actionLabel: 'View Dashboard'
        });
      }

      setSuggestions(generatedSuggestions.filter(s => !dismissed.has(s.id)));
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const dismissSuggestion = (id) => {
    setDismissed(prev => new Set([...prev, id]));
    setSuggestions(prev => prev.filter(s => s.id !== id));
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'border-red-500 bg-red-500/10';
      case 'medium': return 'border-yellow-500 bg-yellow-500/10';
      case 'low': return 'border-blue-500 bg-blue-500/10';
      default: return 'border-gray-500 bg-gray-500/10';
    }
  };

  if (!isAuthenticated) return null;
  if (suggestions.length === 0) return null;

  if (minimized) {
    return (
      <button
        onClick={() => setMinimized(false)}
        className="fixed bottom-6 right-6 bg-zinc-800 border border-zinc-700 text-amber-500 p-3 rounded-full shadow-xl hover:bg-zinc-700 transition-all z-40"
        title="LEE has suggestions for you"
      >
        <Sparkles className="w-5 h-5" />
        {suggestions.length > 0 && (
          <span className="absolute -top-1 -right-1 bg-amber-500 text-black text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
            {suggestions.length}
          </span>
        )}
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-80 z-40 space-y-2 animate-scale-in">
      {/* Header */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-t-xl p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-500" />
          <span className="text-sm font-semibold text-white">LEE Suggests</span>
          <span className="text-[10px] bg-amber-500/10 text-amber-500 px-1.5 py-0.5 rounded-full">{suggestions.length}</span>
        </div>
        <button
          onClick={() => setMinimized(true)}
          className="hover:bg-zinc-800 p-1 rounded transition-colors text-zinc-500 hover:text-white"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Suggestions */}
      <div className="space-y-1.5 max-h-64 overflow-y-auto">
        {suggestions.map((suggestion) => {
          const Icon = suggestion.icon;
          return (
            <div
              key={suggestion.id}
              className="bg-zinc-900/95 border border-zinc-800 p-3 rounded-lg"
            >
              <div className="flex items-start gap-2.5">
                <Icon className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <h4 className="text-white font-medium text-xs mb-0.5">{suggestion.title}</h4>
                  <p className="text-zinc-500 text-[11px] mb-2">{suggestion.description}</p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={suggestion.action}
                      className="flex items-center gap-1 px-2.5 py-1 bg-amber-500 hover:bg-amber-400 text-black text-[10px] font-medium rounded transition-colors"
                    >
                      {suggestion.actionLabel}
                      <ChevronRight className="w-2.5 h-2.5" />
                    </button>
                    <button
                      onClick={() => dismissSuggestion(suggestion.id)}
                      className="px-2 py-1 text-zinc-600 hover:text-zinc-400 text-[10px] transition-colors"
                    >
                      Dismiss
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProactiveAssistant;
